import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TangoService, TangoGrid, TangoSymbol, Constraint } from '../services/tango.service';
import { LanguageService } from '../services/language.service';
import { SeoService } from '../services/seo.service';
import { AdSenseBlockComponent } from '../shared/adsense-block/adsense-block.component';

@Component({
  selector: 'app-tango',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, AdSenseBlockComponent],
  template: `
    <div class="game-container">
      <div class="game-ad-container">
        <app-adsense-block [adSlot]="'9526827533'"></app-adsense-block>
      </div>

      <header class="game-header">
        <button class="btn-back" [routerLink]="['/']">
          <span class="material-symbols-outlined">arrow_back_ios</span>
        </button>
        <h1>{{ t('tango_puzzle') }}</h1>
        <div class="timer" [class.stopped]="gameWon">
          <span class="material-symbols-outlined">{{ gameWon ? 'check_circle' : 'timer' }}</span>
          {{ formatTime(seconds) }}
        </div>
      </header>

      <main class="game-content">
        <div class="controls glass-card">
          <!-- Settings Section (Hidden by default on mobile during game) -->
          <!-- Settings Section (Hidden by default) -->
          <div class="settings-panel" [class.expanded]="showSettings">
            <div class="setting-group">
              <label>{{ t('size') }}</label>
              <div class="difficulty-selector">
                <button (click)="startNewGame(6, difficulty)" [class.active]="size === 6">6x6</button>
                <button (click)="startNewGame(8, difficulty)" [class.active]="size === 8">8x8</button>
              </div>
            </div>

            <div class="setting-group">
              <label>{{ t('difficulty') }}</label>
              <select class="premium-select" [(ngModel)]="difficulty" (change)="startNewGame(size, difficulty)">
                <option value="easy">{{ t('easy') }}</option>
                <option value="medium">{{ t('medium') }}</option>
                <option value="hard">{{ t('hard') }}</option>
              </select>
            </div>

            <button class="btn-primary" (click)="startNewGame(size, difficulty)">
              {{ t('restart') }}
            </button>

            <button class="btn-secondary" (click)="showRules = !showRules">
              <span class="material-symbols-outlined">help</span>
              {{ t('how_to_play') }}
            </button>

            <!-- Mobile Only Footer Links (Inside Menu) -->
            <div class="mobile-footer-links">
              <a [routerLink]="['/privacy-policy']">{{ t('privacy_title') }}</a>
              <span class="separator">•</span>
              <a [routerLink]="['/contact']">{{ t('contact_title') }}</a>
            </div>
          </div>
          
          <!-- Gameplay Actions (Always visible) -->
          <div class="game-actions" *ngIf="gameStarted">
            <button class="action-btn" (click)="getHint()">
              <span class="material-symbols-outlined">lightbulb</span>
              {{ t('hint') }}
            </button>
            <button class="action-btn" (click)="undo()" [disabled]="history.length === 0">
              <span class="material-symbols-outlined">undo</span>
              {{ t('undo') }}
            </button>
            <button class="action-btn" (click)="clearGrid()">
              <span class="material-symbols-outlined">delete_sweep</span>
              {{ t('clear') }}
            </button>
            <!-- Mobile Menu Toggle -->
            <button class="action-btn mobile-menu-btn" (click)="showSettings = !showSettings" [class.active]="showSettings">
              <span class="material-symbols-outlined">{{ showSettings ? 'close' : 'settings' }}</span>
              Menu
            </button>
          </div>

          <!-- Hint Box with Slide Animation: Always in DOM, controlled by class -->
          <div class="hint-box" [class.visible]="!!hintText">
            <p>{{ hintText }}</p>
          </div>
        </div>

        <div class="board-wrapper">
          <div class="tango-board-container" [style.--size]="size">
            <div class="tango-game-board" [style.grid-template-columns]="'repeat(' + size + ', 1fr)'">
              <ng-container *ngFor="let row of grid; let rIdx = index; trackBy: trackByIndex">
                <div *ngFor="let cell of row; let cIdx = index; trackBy: trackByIndex" 
                     class="tango-cell"
                     [class.initial]="isInitial[rIdx][cIdx]"
                     [class.hinted]="hintedCell?.r === rIdx && hintedCell?.c === cIdx"
                     [class.error]="isError(rIdx, cIdx)"
                     (click)="cycleSymbol(rIdx, cIdx)">
                  <span class="material-symbols-outlined icon-sun" *ngIf="cell === 'sun'">sunny</span>
                  <span class="material-symbols-outlined icon-moon" *ngIf="cell === 'moon'">dark_mode</span>

                  <!-- Horizontal Constraint (to the right of cell) -->
                  <div class="constraint horizontal" *ngIf="cIdx < size - 1 && hConstraints[rIdx][cIdx]">
                    <span class="sign" [class.diff]="hConstraints[rIdx][cIdx] === 'not-equal'">
                      {{ hConstraints[rIdx][cIdx] === 'equal' ? '=' : '×' }}
                    </span>
                  </div>

                  <!-- Vertical Constraint (below cell) -->
                  <div class="constraint vertical" *ngIf="rIdx < size - 1 && vConstraints[rIdx][cIdx]">
                    <span class="sign" [class.diff]="vConstraints[rIdx][cIdx] === 'not-equal'">
                      {{ vConstraints[rIdx][cIdx] === 'equal' ? '=' : '×' }}
                    </span>
                  </div>
                </div>
              </ng-container>
            </div>
          </div>

        </div>
      </main>

      <div class="rules-overlay" *ngIf="showRules" (click)="showRules = false">
        <div class="rules-modal" (click)="$event.stopPropagation()">
          <div class="modal-header">
            <h3>{{ t('how_to_play') }}</h3>
            <button class="btn-close" (click)="showRules = false">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>
          
          <div class="rules-section">
            <h4>{{ t('tango_goal_title') }}</h4>
            <p>{{ t('tango_goal_text') }}</p>
          </div>

          <div class="rules-section">
            <h4>{{ t('tango_rules_title') }}</h4>
            <ul>
              <li>{{ t('tango_rule_1') }}</li>
              <li>{{ t('tango_rule_2') }}</li>
              <li><b>=</b> {{ t('tango_rule_3') }}</li>
              <li><b>×</b> {{ t('tango_rule_4') }}</li>
            </ul>
          </div>

          <div class="rules-section">
            <h4>{{ t('tango_how_title') }}</h4>
            <p>{{ t('tango_how_text') }}</p>
          </div>

          <div class="rules-note">
            <p><strong>Note:</strong> {{ t('tango_rules_note') }}</p>
          </div>
        </div>
      </div>

      <div class="win-modal" *ngIf="gameWon">
        <div class="modal-content glass-card">
          <span class="material-symbols-outlined icon-win">emoji_events</span>
          <h2>{{ t('victory') }}</h2>
          <p>{{ t('tango_win_msg') }} {{ t('time') }} : {{ formatTime(seconds) }}</p>
          <button class="btn-restart" (click)="startNewGame(size, difficulty)">{{ t('restart') }}</button>
        </div>
      </div>

       <footer class="game-footer">
        <a [routerLink]="['/privacy-policy']">{{ t('privacy_title') }}</a>
        <span class="separator">•</span>
        <a [routerLink]="['/contact']">{{ t('contact_title') }}</a>
      </footer>
    </div>
  `
})
export class TangoComponent implements OnInit, OnDestroy {
  size = 6;
  difficulty: 'easy' | 'medium' | 'hard' = 'medium';
  _showRules = false;

  get showRules() { return this._showRules; }
  set showRules(v: boolean) {
    this._showRules = v;
    if (isPlatformBrowser(this.platformId)) {
      if (v) document.body.classList.add('modal-open');
      else document.body.classList.remove('modal-open');
    }
  }

  showSettings = false;
  grid: TangoGrid = [];
  solution: ('sun' | 'moon')[][] = [];
  hConstraints: Constraint[][] = [];
  vConstraints: Constraint[][] = [];
  isInitial: boolean[][] = [];
  hintedCell: { r: number, c: number } | null = null;
  hintText: string | null = null;
  errors: { row: number, col: number }[] = [];

  seconds = 0;
  timerInterval: any;
  gameStarted = false;
  gameWon = false;
  history: { r: number, c: number, prevValue: TangoSymbol | null }[] = [];
  errorMatrix: boolean[][] = [];
  validateTimeout: any;

  constructor(
    private tangoService: TangoService,
    private langService: LanguageService,
    private seoService: SeoService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  t(key: string, params: any = {}) {
    return this.langService.translate(key, params);
  }

  trackByIndex(index: number, obj: any): any {
    return index;
  }

  ngOnInit() {
    this.langService.currentLang$.subscribe(lang => {
      if (lang === 'fr') {
        this.seoService.setPageData({
          title: 'Entraînement Tango - Inspiré des Jeux LinkedIn (Sun & Moon)',
          description: 'Entraînez-vous au jeu Tango (Sun & Moon) type LinkedIn. Grilles illimitées pour progresser. Le meilleur site d\'entraînement aux jeux de logique.',
          keywords: ['tango linkedin', 'entraînement tango', 'jeu sun moon', 'puzzle logique', 'linkedin games', 'jeu de réflexion'],
          type: 'game',
          image: 'assets/tango-preview.png'
        });
      } else {
        this.seoService.setPageData({
          title: 'Tango Training - Inspired by LinkedIn Games (Sun & Moon)',
          description: 'Practice the popular Tango (Sun & Moon) logic puzzle style seen on LinkedIn. Unlimited grids to train your skills. The best Tango training site.',
          keywords: ['tango puzzle', 'linkedin tango', 'linkedin games', 'sun and moon', 'tango training', 'logic game'],
          type: 'game',
          image: 'assets/tango-preview.png'
        });
      }
    });
    if (isPlatformBrowser(this.platformId)) {
      this.startNewGame(6);
    }
  }


  ngOnDestroy() {
    this.stopTimer();
  }

  startNewGame(size: number, difficulty: 'easy' | 'medium' | 'hard' = 'medium') {
    this.size = size;
    this.difficulty = difficulty;
    const puzzleData = this.tangoService.generatePuzzle(size, difficulty);
    this.grid = puzzleData.puzzle;
    this.solution = puzzleData.solution;
    this.hConstraints = puzzleData.hConstraints;
    this.vConstraints = puzzleData.vConstraints;

    this.isInitial = this.grid.map(row => row.map(cell => cell !== null));
    this.history = [];
    this.hintedCell = null;
    this.hintText = null;
    this.errors = [];
    this.gameStarted = true;
    this.gameWon = false;
    this.errorMatrix = Array.from({ length: size }, () => Array(size).fill(false));

    this.resetTimer();
    this.startTimer();
    this.validate(true);
  }

  cycleSymbol(r: number, c: number) {
    if (!this.gameStarted || this.isInitial[r][c] || this.gameWon) return;

    this.hintedCell = null;
    this.hintText = null;
    this.history.push({ r, c, prevValue: this.grid[r][c] });

    const current = this.grid[r][c];
    if (current === null) this.grid[r][c] = 'sun';
    else if (current === 'sun') this.grid[r][c] = 'moon';
    else this.grid[r][c] = null;

    this.validate(false);
  }

  validate(immediate: boolean = false) {
    if (this.validateTimeout) clearTimeout(this.validateTimeout);

    const { valid, errors } = this.tangoService.validateGrid(this.grid, this.size, this.hConstraints, this.vConstraints);

    // Immediate clear of fixed errors
    const errorSet = new Set(errors.map(e => `${e.row},${e.col}`));
    for (let r = 0; r < this.size; r++) {
      for (let c = 0; c < this.size; c++) {
        if (this.errorMatrix[r][c] && !errorSet.has(`${r},${c}`)) {
          this.errorMatrix[r][c] = false;
        }
      }
    }

    const apply = () => {
      this.errorMatrix = Array.from({ length: this.size }, () => Array(this.size).fill(false));
      errors.forEach(e => this.errorMatrix[e.row][e.col] = true);
      this.errors = errors;

      if (valid && !this.gameWon) {
        this.gameWon = true;
        this.gameStarted = false;
        this.stopTimer();
      }
    };

    if (immediate || valid) apply();
    else this.validateTimeout = setTimeout(apply, 400);
  }

  getHint() {
    if (!this.gameStarted || this.gameWon) return;
    const hint = this.tangoService.getHint(this.grid, this.solution, this.size, this.hConstraints, this.vConstraints);
    if (hint) {
      this.hintedCell = { r: hint.row, c: hint.col };
      this.hintText = hint.explanation;
      this.grid[hint.row][hint.col] = hint.symbol;
      this.validate(true);
    }
  }

  undo() {
    const last = this.history.pop();
    if (last) {
      this.grid[last.r][last.c] = last.prevValue;
      this.validate(true);
    }
  }

  clearGrid() {
    for (let r = 0; r < this.size; r++) {
      for (let c = 0; c < this.size; c++) {
        if (!this.isInitial[r][c]) this.grid[r][c] = null;
      }
    }
    this.history = [];
    this.validate(true);
  }

  isError(r: number, c: number) { return this.errorMatrix[r] ? this.errorMatrix[r][c] : false; }
  formatTime(s: number) { return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`; }
  startTimer() {
    this.stopTimer();
    if (isPlatformBrowser(this.platformId)) {
      this.timerInterval = setInterval(() => this.seconds++, 1000);
    }
  }
  stopTimer() { if (this.timerInterval) clearInterval(this.timerInterval); }
  resetTimer() { this.seconds = 0; }
}
